import React, { FC, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryParam, StringParam } from 'use-query-params';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { clientService } from '../../../../api';
import { useUi } from '../../../../context/ui.context';
import { TopProgress, Input, Select, CheckBox, RadioButtonGroup, Button, ClipBoardWrapper } from '../../../../components';
import TextEditor from '../../../../components/text-editor';
import Typography from '../../../../components/typography';
import { icons } from '../../../../icons';
import StyledIcon from '../../../../components/styled-icon';
import {
  Container,
  FormWrapper,
  FormRow,
  FormColumn,
  HelpColumn,
  HelpSection,
  FileUploadContainer,
  FileInputWrapper,
  FileNameDisplay,
  FileActions,
  ChipContainer,
  Chip,
  ColorPickerWrapper,
  ButtonsWrapper,
  ColorPickerSection,
  ColorPickerHeader,
  ColorInputsRow,
  ColorFieldGroup,
  ColorLabelRow,
  ColorValue,
  NativeColorInput,
  Swatches,
  SwatchButton,
  PreviewWrapper,
  ScheduleSection,
  TimePickerContainer,
  TimePickerWrapper,
  ScheduleHelper,
} from './styles';
import TopPanelNotifications from '../slider';
import type { PanelNotification } from '../slider';
import DatePickerCustom from '../../../../components/date-picker';
import { TimeInput } from '@heroui/date-input';
import { parseDate, parseTime } from '@internationalized/date';

const DEFAULT_BACKGROUND_COLORS = [
  '#658DFE',
  '#4671F6',
  '#0F68E9',
  '#F1F4F9',
  '#F9FAFE',
  '#B5CAFD',
  '#229B61',
  '#FFE5E5',
];

const DEFAULT_TEXT_COLORS = [
  '#FFFFFF',
  '#F9FAFE',
  '#1E1E1E',
  '#083170',
  '#001E45',
  '#E85048',
];

const DEFAULT_BACKGROUND_COLOR = '#658DFE';
const DEFAULT_TEXT_COLOR = '#FFFFFF';

const isValidHexColor = (value?: string) =>
  typeof value === 'string' && /^#[0-9A-Fa-f]{6}$/.test(value);

const pad = (value: number) => value.toString().padStart(2, '0');

const normalizeTimeString = (time?: string) => {
  if (!time) {
    return '00:00:00';
  }
  const parts = time.split(':').map((part) => part.trim());
  const [hour = '00', minute = '00', second = '00'] = parts;
  return `${pad(parseInt(hour, 10) || 0)}:${pad(parseInt(minute, 10) || 0)}:${pad(parseInt(second, 10) || 0)}`;
};

const combineDateTimeToISO = (date?: string, time?: string) => {
  if (!date) {
    return '';
  }
  const normalizedTime = normalizeTimeString(time);
  const [year, month, day] = date.split('-').map((part) => parseInt(part, 10));
  if (!year || !month || !day) {
    return '';
  }
  const [hours, minutes, seconds] = normalizedTime.split(':').map((part) => parseInt(part, 10));
  const localDate = new Date(year, month - 1, day, hours, minutes, seconds);
  const offsetMinutes = -localDate.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const absOffset = Math.abs(offsetMinutes);
  const offsetHours = pad(Math.floor(absOffset / 60));
  const offsetMins = pad(absOffset % 60);

  return `${year}-${pad(month)}-${pad(day)}T${pad(hours)}:${pad(minutes)}:${pad(seconds)}${sign}${offsetHours}:${offsetMins}`;
};

const getLocalTimezoneOffset = () => {
  const offsetMinutes = -new Date().getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const absOffset = Math.abs(offsetMinutes);
  const offsetHours = pad(Math.floor(absOffset / 60));
  const offsetMins = pad(absOffset % 60);
  return `${sign}${offsetHours}:${offsetMins}`;
};

interface FileUpload {
  file: File | null;
  name: string;
}

const SendMassMessage: FC = () => {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const { viewAlert } = useUi();
  
  const [filter] = useQueryParam('filtering', StringParam);
  const [search] = useQueryParam('search', StringParam);
  const [ordering] = useQueryParam('ordering', StringParam);
  
  const [pageLoading, setPageLoading] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [emailVariables, setEmailVariables] = useState<string[]>([]);
  const [targetedNotifications, setTargetedNotifications] = useState<any>({});
  const [targetedNotificationsOptions, setTargetedNotificationsOptions] = useState<any[]>([]);
  const [maxFileSize, setMaxFileSize] = useState<number>(10485760);
  const [defaultFromEmail, setDefaultFromEmail] = useState<string>('');
  const [files, setFiles] = useState<FileUpload[]>([]);
  const timezoneOffset = useMemo(() => getLocalTimezoneOffset(), []);

  const initialValues = {
    message_type: 'email_and_message',
    targeted_notifications: '',
    top_line_notification: false,
    show_top_line_until: '',
    show_top_line_until_date: '',
    show_top_line_until_time: '',
    top_line_background_color: DEFAULT_BACKGROUND_COLOR,
    top_line_text_color: DEFAULT_TEXT_COLOR,
    from_name: '',
    from_email: '',
    subject: '',
    body: '',
    send_batch_size: 16,
    send_interval: 30,
  };

  useEffect(() => {
    getOptions();
  }, []);

  const getOptions = async () => {
    try {
      const res = await clientService.getOptions();
      setEmailVariables(res.data.email_variables || []);
      setTargetedNotifications(res.data.targeted_notifications || {});
      
      const notificationsArray = Object.keys(res.data.targeted_notifications || {}).map(key => ({
        label: res.data.targeted_notifications[key],
        value: key,
      }));
      setTargetedNotificationsOptions(notificationsArray);
      
      setMaxFileSize(res.data.max_email_attachment_size || 10485760);
      setDefaultFromEmail(res.data.default_from_email || '');
      setFieldValue('from_email', res.data.default_from_email || '');
      setPageLoading(false);
    } catch (err: any) {
      viewAlert({
        severity: 'error',
        message: err?.response?.data?.detail || `${t('ERROR_OCCURRED')}`,
      });
      setPageLoading(false);
    }
  };

  const onSubmit = async (values: any) => {
    setLoading(true);
    
    const formData = new FormData();
    
    formData.append('message_type', values.message_type);
    formData.append('targeted_notifications', values.targeted_notifications);
    formData.append('top_line_notification', values.top_line_notification.toString());
    formData.append('show_top_line_until_time', values.show_top_line_until_time || '');
    formData.append('show_top_line_until', values.show_top_line_until || '');
    formData.append('top_line_background_color', values.top_line_background_color);
    formData.append('top_line_text_color', values.top_line_text_color);
    formData.append('from_name', values.from_name);
    formData.append('from_email', values.from_email);
    formData.append('subject', values.subject);
    formData.append('body', values.body);
    formData.append('send_batch_size', values.send_batch_size.toString());
    formData.append('send_interval', values.send_interval.toString());
    formData.append('filter', filter || '');
    formData.append('search', search || '');

    files.forEach((fileItem, index) => {
      if (fileItem.file) {
        formData.append(`file_data_${index}`, fileItem.file);
        formData.append(`file_name_${index}`, fileItem.name);
      }
    });

    try {
      await clientService.sendMassMessage(formData);
      viewAlert({
        severity: 'success',
        message: 'Message sent successfully',
      });
      setTimeout(() => {
        navigate(`/clients-users/mass-message?filtering=${filter || ''}&search=${search || ''}&ordering=${ordering || ''}`);
      }, 1000);
    } catch (err: any) {
      viewAlert({
        severity: 'error',
        message: err?.response?.data?.detail || `${t('ERROR_OCCURRED')}`,
      });
      setLoading(false);
    }
  };

  const {
    values,
    errors,
    touched,
    handleSubmit,
    handleChange,
    handleBlur,
    setFieldValue,
  } = useFormik({
    enableReinitialize: true,
    validationSchema: yup.object().shape({
      message_type: yup.string().required(`${t('REQUIRED_FIELD')}`),
      from_email: yup.string().email(`${t('NOT_VALID_EMAIL')}`).required(`${t('REQUIRED_FIELD')}`),
      subject: yup.string().required(`${t('REQUIRED_FIELD')}`),
      body: yup.string().required(`${t('REQUIRED_FIELD')}`),
      send_batch_size: yup.number().required(`${t('REQUIRED_FIELD')}`).min(1, `${t('MIN_VALUE')} 1`),
      send_interval: yup.number().required(`${t('REQUIRED_FIELD')}`).min(1, `${t('MIN_VALUE')} 1`),
    }),
    initialValues,
    onSubmit,
  });

  const datePickerValue = useMemo(() => {
    if (!values.show_top_line_until_date) {
      return null;
    }
    try {
      return parseDate(values.show_top_line_until_date);
    } catch (error) {
      return null;
    }
  }, [values.show_top_line_until_date]);

  const timeInputValue = useMemo(() => {
    if (!values.show_top_line_until_time) {
      return null;
    }
    try {
      return parseTime(normalizeTimeString(values.show_top_line_until_time));
    } catch (error) {
      return null;
    }
  }, [values.show_top_line_until_time]);

  useEffect(() => {
    if (!values.top_line_notification) {
      return;
    }
    if (!isValidHexColor(values.top_line_background_color)) {
      setFieldValue('top_line_background_color', DEFAULT_BACKGROUND_COLOR);
    }
    if (!isValidHexColor(values.top_line_text_color)) {
      setFieldValue('top_line_text_color', DEFAULT_TEXT_COLOR);
    }
  }, [
    setFieldValue,
    values.top_line_background_color,
    values.top_line_notification,
    values.top_line_text_color,
  ]);

  useEffect(() => {
    const isoValue = combineDateTimeToISO(
      values.show_top_line_until_date,
      values.show_top_line_until_time
    );
    if (isoValue !== values.show_top_line_until) {
      setFieldValue('show_top_line_until', isoValue);
    }
  }, [
    setFieldValue,
    values.show_top_line_until,
    values.show_top_line_until_date,
    values.show_top_line_until_time,
  ]);

  useEffect(() => {
    if (values.top_line_notification) {
      return;
    }
    if (values.show_top_line_until_date) {
      setFieldValue('show_top_line_until_date', '');
    }
    if (values.show_top_line_until_time) {
      setFieldValue('show_top_line_until_time', '');
    }
    if (values.show_top_line_until) {
      setFieldValue('show_top_line_until', '');
    }
  }, [
    setFieldValue,
    values.show_top_line_until,
    values.show_top_line_until_date,
    values.show_top_line_until_time,
    values.top_line_notification,
  ]);

  const safeBackgroundColor = useMemo(
    () =>
      isValidHexColor(values.top_line_background_color)
        ? values.top_line_background_color
        : DEFAULT_BACKGROUND_COLOR,
    [values.top_line_background_color]
  );

  const safeTextColor = useMemo(
    () =>
      isValidHexColor(values.top_line_text_color)
        ? values.top_line_text_color
        : DEFAULT_TEXT_COLOR,
    [values.top_line_text_color]
  );

  const previewNotification = useMemo<PanelNotification[]>(
    () => [
      {
        id: 'preview',
        title: values.subject || 'Panel notification preview',
        body:
          values.body ||
          '<p>Your message preview will appear here once you start typing.</p>',
        top_line_background_color: safeBackgroundColor,
        top_line_text_color: safeTextColor,
      },
    ],
    [safeBackgroundColor, safeTextColor, values.body, values.subject]
  );

  const formikProps = {
    errors,
    values,
    touched,
    handleChange,
    handleBlur,
  };

  const handleFileSelect = (index: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      if (selectedFile.size > maxFileSize) {
        viewAlert({
          severity: 'error',
          message: `File size exceeds ${(maxFileSize / 1024 / 1024).toFixed(0)} MB`,
        });
        return;
      }
      const newFiles = [...files];
      newFiles[index] = {
        file: selectedFile,
        name: selectedFile.name,
      };
      setFiles(newFiles);
    }
    event.target.value = '';
  };

  const handleRemoveFile = (index: number) => {
    const newFiles = files.filter((_, i) => i !== index);
    setFiles(newFiles);
  };

  const handleAddFile = () => {
    setFiles([...files, { file: null, name: '' }]);
  };


  const handleCancel = () => {
    navigate(`/clients-users/mass-message?filtering=${filter || ''}&search=${search || ''}&ordering=${ordering || ''}`);
  };

  const messageTypeButtons = [
    { title: 'Send mass email and panel notification message', value: 'email_and_message', id: '1' },
    { title: 'Send panel notification message', value: 'message', id: '2' },
    { title: 'Send mass email', value: 'email', id: '3' },
  ];

  if (pageLoading) return <TopProgress loading={pageLoading} />;

  return (
    <Container>
      <FormWrapper>
        <Typography variant="h1" customStyles={{ marginBottom: '24px' }}>
          Send mass email and/or panel notification message to selected clients
        </Typography>

        <FormRow>
          <FormColumn flex={2}>
            {/* Message Type */}
            <RadioButtonGroup
              title="Message type"
              buttons={messageTypeButtons}
              selectedButton={values.message_type}
              onChange={(value) => setFieldValue('message_type', value)}
            />

            {/* Targeted Notifications */}
            <Select
              title="Notification category"
              data={[{ label: 'None', value: '' }, ...targetedNotificationsOptions]}
              selectedValue={values.targeted_notifications}
              onChange={(value) => setFieldValue('targeted_notifications', value)}
              placeholder="Select notification category"
              fieldName="targeted_notifications"
              {...formikProps}
            />

            {/* Top Line Notification */}
            <CheckBox
              title="Show top line notification in enduser panel"
              value={values.top_line_notification}
              onChange={() => setFieldValue('top_line_notification', !values.top_line_notification)}
            />

            {/* Color Pickers */}
            {values.top_line_notification && (
              <ColorPickerSection>
                <ColorPickerHeader>
                  <Typography variant="h3">
                    Top line appearance
                  </Typography>
                  <Typography variant="body">
                    Pick colors that match the product palette or use one of the presets.
                  </Typography>
                </ColorPickerHeader>
                <ColorInputsRow>
                  <ColorFieldGroup>
                    <ColorLabelRow>
                      <Typography variant="body" customStyles={{ fontWeight: 600 }}>
                        Background color
                      </Typography>
                      <ColorValue>{safeBackgroundColor}</ColorValue>
                    </ColorLabelRow>
                    <NativeColorInput
                      value={safeBackgroundColor}
                      onChange={(event) =>
                        setFieldValue('top_line_background_color', event.target.value)
                      }
                      aria-label="Top line background color"
                    />
                    <Swatches>
                      {DEFAULT_BACKGROUND_COLORS.map((color) => (
                        <SwatchButton
                          key={color}
                          type="button"
                          color={color}
                          isActive={color.toLowerCase() === safeBackgroundColor.toLowerCase()}
                          onClick={() => setFieldValue('top_line_background_color', color)}
                          aria-label={`Select background color ${color}`}
                        />
                      ))}
                    </Swatches>
                  </ColorFieldGroup>
                  <ColorFieldGroup>
                    <ColorLabelRow>
                      <Typography variant="body" customStyles={{ fontWeight: 600 }}>
                        Text color
                      </Typography>
                      <ColorValue>{safeTextColor}</ColorValue>
                    </ColorLabelRow>
                    <NativeColorInput
                      value={safeTextColor}
                      onChange={(event) =>
                        setFieldValue('top_line_text_color', event.target.value)
                      }
                      aria-label="Top line text color"
                    />
                    <Swatches>
                      {DEFAULT_TEXT_COLORS.map((color) => (
                        <SwatchButton
                          key={color}
                          type="button"
                          color={color}
                          isActive={color.toLowerCase() === safeTextColor.toLowerCase()}
                          onClick={() => setFieldValue('top_line_text_color', color)}
                          aria-label={`Select text color ${color}`}
                        />
                      ))}
                    </Swatches>
                  </ColorFieldGroup>
                </ColorInputsRow>
                <ScheduleSection>
                  <Typography variant="h3">
                    Display window
                  </Typography>
                  <Typography variant="body">
                    Choose when the notification bar should disappear for end users.
                  </Typography>
                  <ColorInputsRow>
                    <ColorFieldGroup>
                      <DatePickerCustom
                        title="Show top line until date"
                        value={datePickerValue ?? null}
                        onChange={(dateValue) =>
                          setFieldValue(
                            'show_top_line_until_date',
                            dateValue ? dateValue.toString() : ''
                          )
                        }
                        hideTimeZone
                        granularity="day"
                        showMonthAndYearPickers
                      />
                    </ColorFieldGroup>
                    <ColorFieldGroup>
                      <TimePickerContainer>
                        <Typography variant="body" customStyles={{ fontWeight: 600 }}>
                          Show top line until time
                        </Typography>
                        <TimePickerWrapper>
                          <TimeInput
                            value={timeInputValue ?? null}
                            onChange={(timeValue) =>
                              setFieldValue(
                                'show_top_line_until_time',
                                timeValue ? timeValue.toString() : ''
                              )
                            }
                            granularity="second"
                            hourCycle={24}
                          />
                        </TimePickerWrapper>
                        <ScheduleHelper>
                          Uses your current timezone offset ({timezoneOffset}). Leave empty to keep midnight.
                        </ScheduleHelper>
                      </TimePickerContainer>
                    </ColorFieldGroup>
                  </ColorInputsRow>
                  <Input
                    title="Computed end time (ISO 8601)"
                    fieldName="show_top_line_until"
                    placeholder="YYYY-MM-DDTHH:mm:ss+03:00"
                    readOnly
                    {...formikProps}
                  />
                </ScheduleSection>
                <PreviewWrapper>
                  <Typography variant="h3">
                    Preview
                  </Typography>
                  <TopPanelNotifications notifications={previewNotification} interval={8000} />
                </PreviewWrapper>
              </ColorPickerSection>
            )}

            {/* From Name */}
            <Input
              title="From name"
              fieldName="from_name"
              placeholder="From name"
              {...formikProps}
            />

            {/* From Email */}
            <Input
              title="From email"
              fieldName="from_email"
              placeholder="From email"
              required
              {...formikProps}
            />

            {/* Subject */}
            <Input
              title="Subject"
              fieldName="subject"
              placeholder="Subject"
              required
              {...formikProps}
            />

            {/* Message Body */}
            <div>
              <Typography variant="h3" customStyles={{ marginBottom: '12px' }}>
                Message body
              </Typography>
              <TextEditor
                fieldName="body"
                {...formikProps}
                onChange={(value: string) => setFieldValue('body', value)}
              />
            </div>

            {/* File Uploads */}
            <div>
              <Typography variant="h3" customStyles={{ marginBottom: '12px' }}>
                Add attachments
              </Typography>
              <FileUploadContainer>
                {files.map((fileItem, index) => (
                  <FileInputWrapper key={index}>
                    <input
                      type="file"
                      id={`file-input-${index}`}
                      style={{ display: 'none' }}
                      onChange={handleFileSelect(index)}
                    />
                    <FileNameDisplay>
                      {fileItem.name || 'No file chosen'}
                    </FileNameDisplay>
                    <FileActions>
                      <label 
                        htmlFor={`file-input-${index}`}
                        style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                      >
                        <StyledIcon src={icons.PlusDefault} />
                      </label>
                      <Button
                        variant="icon"
                        size="small"
                        onClick={() => handleRemoveFile(index)}
                      >
                        <StyledIcon src={icons.CloseIcon} />
                      </Button>
                    </FileActions>
                  </FileInputWrapper>
                ))}
                <Button
                  variant="stroke"
                  size="display"
                  icon={icons.PlusDefault}
                  title="Add attachment"
                  onClick={handleAddFile}
                  customStyles={{ marginTop: '12px' }}
                />
              </FileUploadContainer>
            </div>

            {/* Batch Settings */}
            <ColorPickerWrapper>
              <Input
                title="Send batch size"
                fieldName="send_batch_size"
                placeholder="16"
                type="number"
                required
                {...formikProps}
              />
              <Input
                title="Send interval between batches (seconds)"
                fieldName="send_interval"
                placeholder="30"
                type="number"
                required
                {...formikProps}
              />
            </ColorPickerWrapper>
          </FormColumn>

          {/* Help Column */}
          <HelpColumn>
            <HelpSection>
              <Typography variant="h3" customStyles={{ marginBottom: '12px' }}>
                Send mass email/panel notification
              </Typography>
              <Typography variant="body" customStyles={{ marginBottom: '16px' }}>
                You can choose to either send emails, send a notification in enduser panel
                (notifications page) or both. You can even show the notification as a top
                line over the app if you choose an option that involves panel notification.
              </Typography>
            </HelpSection>

            <HelpSection>
              <Typography variant="h3" customStyles={{ marginBottom: '12px' }}>
                Message type
              </Typography>
              <Typography variant="body" customStyles={{ marginBottom: '16px' }}>
                If user notifications settings do not have the target category notifications
                enabled, he won't receive this kind of notification. A value of "none" will
                send the notification regardless of user settings.
              </Typography>
            </HelpSection>

            <HelpSection>
              <Typography variant="h3" customStyles={{ marginBottom: '12px' }}>
                Email variables
              </Typography>
              <Typography variant="body" customStyles={{ marginBottom: '12px' }}>
                The following parameters can be used to customize email body:
              </Typography>
              <ChipContainer>
                {emailVariables.map((variable) => (
                  <ClipBoardWrapper key={variable} toCopy={variable}>
                    <Chip>
                      {variable}
                    </Chip>
                  </ClipBoardWrapper>
                ))}
              </ChipContainer>
            </HelpSection>
          </HelpColumn>
        </FormRow>

        <ButtonsWrapper>
          <Button
            variant="secondary"
            size="display"
            title="Cancel"
            onClick={handleCancel}
          />
          <Button
            variant="primary"
            size="display"
            title="Send messages"
            onClick={() => handleSubmit()}
            loading={loading}
          />
        </ButtonsWrapper>
      </FormWrapper>
    </Container>
  );
};

export default SendMassMessage;
